import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmissionsChart = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('12m');
  const [selectedSources, setSelectedSources] = useState(['fleet', 'facilities', 'manufacturing']);

  const emissionsData = [
    { month: 'Jan', fleet: 145, facilities: 89, manufacturing: 234, total: 468 },
    { month: 'Feb', fleet: 138, facilities: 92, manufacturing: 221, total: 451 },
    { month: 'Mar', fleet: 142, facilities: 87, manufacturing: 218, total: 447 },
    { month: 'Apr', fleet: 135, facilities: 84, manufacturing: 205, total: 424 },
    { month: 'May', fleet: 129, facilities: 81, manufacturing: 198, total: 408 },
    { month: 'Jun', fleet: 124, facilities: 78, manufacturing: 192, total: 394 },
    { month: 'Jul', fleet: 118, facilities: 75, manufacturing: 185, total: 378 },
    { month: 'Aug', fleet: 115, facilities: 73, manufacturing: 179, total: 367 },
    { month: 'Sep', fleet: 112, facilities: 71, manufacturing: 174, total: 357 },
    { month: 'Oct', fleet: 108, facilities: 69, manufacturing: 168, total: 345 },
    { month: 'Nov', fleet: 105, facilities: 67, manufacturing: 162, total: 334 },
    { month: 'Dec', fleet: 102, facilities: 65, manufacturing: 156, total: 323 }
  ];

  const sourceConfig = [
    { key: 'fleet', name: 'Fleet Operations', color: '#2563EB', icon: 'Truck' },
    { key: 'facilities', name: 'Facilities', color: '#10B981', icon: 'Building2' },
    { key: 'manufacturing', name: 'Manufacturing', color: '#F59E0B', icon: 'Factory' }
  ];

  const timeframes = [
    { value: '3m', label: '3M' },
    { value: '6m', label: '6M' },
    { value: '12m', label: '12M' },
    { value: '24m', label: '24M' }
  ];

  const toggleSource = (sourceKey) => {
    setSelectedSources(prev => 
      prev?.includes(sourceKey) 
        ? prev?.filter(s => s !== sourceKey)
        : [...prev, sourceKey]
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const total = payload?.reduce((sum, entry) => sum + entry?.value, 0);
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground mb-2">{label} 2024</p>
          {payload?.map((entry) => (
            <div key={entry?.dataKey} className="flex items-center justify-between space-x-4 text-sm">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry?.color }}
                ></div>
                <span className="text-muted-foreground">{entry?.name}:</span>
              </div>
              <span className="font-medium text-foreground">{entry?.value} tCO2e</span>
            </div>
          ))}
          <div className="border-t border-border mt-2 pt-2">
            <div className="flex items-center justify-between text-sm font-medium">
              <span className="text-foreground">Total:</span>
              <span className="text-foreground">{total} tCO2e</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div className="flex items-center space-x-3 mb-4 lg:mb-0">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="BarChart3" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Carbon Emissions by Source</h3>
            <p className="text-sm text-muted-foreground">Monthly tracking with trend analysis</p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
            {timeframes?.map((timeframe) => (
              <button
                key={timeframe?.value}
                onClick={() => setSelectedTimeframe(timeframe?.value)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  selectedTimeframe === timeframe?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {timeframe?.label}
              </button>
            ))}
          </div>

          <Button variant="outline" iconName="Download" iconPosition="left" className="text-xs">
            Export
          </Button>
        </div>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        {sourceConfig?.map((source) => (
          <button
            key={source?.key}
            onClick={() => toggleSource(source?.key)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all ${
              selectedSources?.includes(source?.key)
                ? 'border-primary bg-primary/5 text-foreground'
                : 'border-border bg-muted/50 text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={source?.icon} size={16} />
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: source?.color }}
            ></div>
            <span className="text-sm font-medium">{source?.name}</span>
          </button>
        ))}
      </div>
      {/* Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={emissionsData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="fleetGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#2563EB" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="facilitiesGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="manufacturingGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              label={{ value: 'tCO2e', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {selectedSources?.includes('manufacturing') && (
              <Area
                type="monotone"
                dataKey="manufacturing"
                stackId="1"
                stroke="#F59E0B"
                fill="url(#manufacturingGradient)"
                strokeWidth={2}
                name="Manufacturing"
              />
            )}
            {selectedSources?.includes('facilities') && (
              <Area
                type="monotone"
                dataKey="facilities"
                stackId="1"
                stroke="#10B981"
                fill="url(#facilitiesGradient)"
                strokeWidth={2}
                name="Facilities"
              />
            )}
            {selectedSources?.includes('fleet') && (
              <Area
                type="monotone"
                dataKey="fleet"
                stackId="1"
                stroke="#2563EB"
                fill="url(#fleetGradient)"
                strokeWidth={2}
                name="Fleet Operations"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <p className="text-2xl font-bold text-success">-23.7%</p>
          <p className="text-sm text-muted-foreground">YoY Reduction</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-foreground">323</p>
          <p className="text-sm text-muted-foreground">Current Month (tCO2e)</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-primary">4,247</p>
          <p className="text-sm text-muted-foreground">YTD Total (tCO2e)</p>
        </div>
      </div>
    </div>
  );
};

export default EmissionsChart;